import { getUrlHostname, isLocalHostname } from './environment.helpers';

/** Hosted Browserless `/function` used on localhost, test, and production. */
export const CLOUD_BROWSERLESS_FUNCTION_URL =
  'https://chrome.browserless.io/function';

/** Reverse-proxy prefix used by `npm run browserless:tunnel` for the API. */
export const PRINT_API_PROXY_PATH = '/api';

/** Live origin file written by the tunnel script (gitignored). */
export const REPORT_PUBLIC_ORIGIN_FILENAME = '.report-public-origin';

export type PreparedReportPrintUrl =
  { kind: 'ok'; url: string } | { kind: 'unreachable' };

/**
 * True when `url` points at this machine (localhost / loopback).
 * Cloud Browserless cannot `page.goto` those hosts.
 */
export const isLoopbackHttpUrl = (url: string): boolean => {
  const hostname = getUrlHostname(url);
  return !!hostname && isLocalHostname(hostname);
};

/**
 * Resolves the Browserless `/function` URL.
 * Same hosted endpoint as test and production unless `explicitUrl` is set.
 */
export const resolveBrowserlessFunctionUrl = (explicitUrl?: string): string =>
  explicitUrl || CLOUD_BROWSERLESS_FUNCTION_URL;

/**
 * Parses a public origin used so cloud Browserless can load localhost.
 * Rejects loopback hosts — they are not reachable from Browserless cloud.
 */
export const parseReportPublicOrigin = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
    if (isLocalHostname(url.hostname)) return undefined;
    return url.origin;
  } catch {
    return undefined;
  }
};

type PrepareReportPrintUrlOptions = {
  /** Public tunnel origin (`REPORT_PUBLIC_ORIGIN` or the live origin file). */
  publicOrigin?: string;
};

/**
 * Rewrites `printUrl` onto a public origin, keeping path and query.
 */
export const replaceReportPrintUrlOrigin = (
  printUrl: string,
  publicOrigin: string,
): string | undefined => {
  const origin = parseReportPublicOrigin(publicOrigin);
  if (!origin) return undefined;
  try {
    const current = new URL(printUrl);
    return new URL(
      `${current.pathname}${current.search}${current.hash}`,
      origin,
    ).toString();
  } catch {
    return undefined;
  }
};

/**
 * Makes a print URL reachable from hosted Browserless Chrome.
 * Loopback URLs are rewritten onto {@link PrepareReportPrintUrlOptions.publicOrigin}
 * when that origin is public; otherwise they are unreachable.
 */
export const prepareReportPrintUrlForBrowserless = (
  printUrl: string,
  options: PrepareReportPrintUrlOptions = {},
): PreparedReportPrintUrl => {
  if (!isLoopbackHttpUrl(printUrl)) return { kind: 'ok', url: printUrl };
  const rewritten = options.publicOrigin
    ? replaceReportPrintUrlOrigin(printUrl, options.publicOrigin)
    : undefined;
  if (rewritten) return { kind: 'ok', url: rewritten };
  return { kind: 'unreachable' };
};

type ResolvePrintSessionApiUrlInput = {
  apiUrl: string;
  /** Full page href so `print=1` and hostname can be read. */
  pageHref?: string;
  isDev: boolean;
};

/**
 * API origin for a Browserless print session on the Vite dev server.
 * A public tunnel uses same-origin `/api` (see `npm run browserless:tunnel`).
 */
export const resolvePrintSessionApiUrl = ({
  apiUrl,
  pageHref,
  isDev,
}: ResolvePrintSessionApiUrlInput): string => {
  if (!isDev || !pageHref) return apiUrl;

  let pageUrl: URL;
  try {
    pageUrl = new URL(pageHref);
  } catch {
    return apiUrl;
  }

  if (pageUrl.searchParams.get('print') !== '1') return apiUrl;
  if (isLocalHostname(pageUrl.hostname)) return apiUrl;

  return `${pageUrl.origin}${PRINT_API_PROXY_PATH}`;
};
